// Receives DOMPurify-cleaned markup. Resource IDs and embedded CSS belong to one SVG.
export function scopeSvg(markup: string, prefix: string): string {
  const doc = new DOMParser().parseFromString(markup, "image/svg+xml");
  const root = doc.documentElement;
  if (root.localName !== "svg") throw new Error("Invalid SVG root");
  root.setAttribute("data-svg-scope", prefix);
  const ids = new Map<string, string>();
  const elements = [root, ...Array.from(root.querySelectorAll("*"))];
  for (const element of elements) {
    const id = element.getAttribute("id");
    if (id) {const scoped = `${prefix}-${ids.size}`; ids.set(id, scoped); element.setAttribute("id", scoped);}
  }
  const rewriteUrls = (value: string) => value.replace(/url\(\s*(["']?)#([^\s)'"\s]+)\1\s*\)/g, (match, quote, id) => ids.has(id) ? `url(#${ids.get(id)})` : match);
  for (const element of elements) {
    for (const attr of Array.from(element.attributes)) {
      if (attr.localName === "id") continue;
      let value = rewriteUrls(attr.value);
      if (attr.localName === "href" && value.startsWith("#") && ids.has(value.slice(1))) value = `#${ids.get(value.slice(1))}`;
      if (attr.localName === "aria-labelledby" || attr.localName === "aria-describedby") value = value.split(/\s+/).map((id) => ids.get(id) ?? id).join(" ");
      if (value !== attr.value) element.setAttributeNS(attr.namespaceURI, attr.name, value);
    }
  }
  const selectorRoot = `:where([data-svg-scope="${prefix}"])`;
  for (const style of Array.from(root.querySelectorAll("style"))) {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(rewriteUrls(style.textContent));
    const scopedRules = (rules: CSSRuleList): string => Array.from(rules).map((rule) => {
      if (rule instanceof CSSStyleRule) {
        const selector = rule.selectorText.replace(/#([\w-]+)/g, (match, id) => ids.has(id) ? `#${ids.get(id)}` : match);
        return `${selectorRoot}:is(${selector}), ${selectorRoot} :is(${selector}) {${rule.style.cssText}}`;
      }
      if (rule instanceof CSSMediaRule) return `@media ${rule.conditionText} {${scopedRules(rule.cssRules)}}`;
      if (rule instanceof CSSSupportsRule) return `@supports ${rule.conditionText} {${scopedRules(rule.cssRules)}}`;
      // Font-face, keyframes and imports have document-wide identity; embedded SVG is static.
      return "";
    }).join("\n");
    style.textContent = scopedRules(sheet.cssRules);
  }
  return new XMLSerializer().serializeToString(root);
}
